'use client';

import Link from 'next/link';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';

interface DiveSite {
  id: string;
  name: string;
  description: string;
  image: string;
  alt: string;
  depth: string;
  difficulty: string;
  type: string;
  regionName: string;
}

interface Species {
  id: number;
  commonName: string;
  scientificName: string;
  category: string;
  image: string;
  alt: string;
  conservationStatus: string;
  sightingProbability: string;
}

interface DiveCentre {
  id: number;
  name: string;
  region: string;
  trainingAgency: string;
  focus: string[];
  website?: string;
  logo?: string;
}

interface Resource {
  id: string;
  name: string;
  description: string;
  link: string;
  logo: string;
  category: string;
}

type SearchResult =
  | { type: 'dive-site'; data: DiveSite }
  | { type: 'species'; data: Species }
  | { type: 'dive-centre'; data: DiveCentre }
  | { type: 'resource'; data: Resource };

interface SearchResultCardProps {
  result: SearchResult;
}

const cardStyle = {
  background: '#0F2234',
  border: '1px solid rgba(255,255,255,0.08)',
  boxShadow: '0 4px 20px rgba(0,0,0,0.30)',
};

const cardHoverEnter = (el: HTMLElement) => {
  el.style.border = '1px solid rgba(111,211,224,0.25)';
  el.style.boxShadow = '0 8px 30px rgba(0,0,0,0.45)';
  el.style.transform = 'translateY(-3px)';
};

const cardHoverLeave = (el: HTMLElement) => {
  el.style.border = '1px solid rgba(255,255,255,0.08)';
  el.style.boxShadow = '0 4px 20px rgba(0,0,0,0.30)';
  el.style.transform = 'translateY(0)';
};

// ─── Branded icon maps ────────────────────────────────────────────────────────

const TYPE_BRANDED_ICONS: Record<string, string> = {
  Wreck: '/assets/images/cyprus-diving-wreck-icon-1777190509956.png',
  Reef: '/assets/images/cyprus-diving-reef-icon-1777190732933.png',
  Cave: '/assets/images/cyprus-diving-cave-icon-1777190748824.png',
  Wall: '/assets/images/cyprus-diving-wall-dropoff-icon-1777190565359.png',
  'Artificial Reef': '/assets/images/cyprus-diving-reef-icon-1777190732933.png',
  Shore: '/assets/images/cyprus-diving-reef-icon-1777190732933.png',
  Technical: '/assets/images/cyprus-diving-technical-icon-1777190696784.png',
};

const TYPE_ACCENT: Record<string, string> = {
  Wreck: '#FF8A3D',
  Reef: '#6FD3E0',
  Cave: '#a78bfa',
  Wall: '#38bdf8',
  'Artificial Reef': '#4ade80',
  Shore: '#6FD3E0',
  Technical: '#f87171',
};

const SearchResultCard = ({ result }: SearchResultCardProps) => {
  // Dive Site Card
  if (result.type === 'dive-site') {
    const site = result.data;
    const brandedIcon = TYPE_BRANDED_ICONS[site.type];
    const accent = TYPE_ACCENT[site.type] ?? '#6FD3E0';
    return (
      <Link href="/regional-dive-sites" className="block group">
        <div
          className="rounded-xl overflow-hidden transition-all duration-200 h-full"
          style={cardStyle}
          onMouseEnter={(e) => cardHoverEnter(e.currentTarget as HTMLElement)}
          onMouseLeave={(e) => cardHoverLeave(e.currentTarget as HTMLElement)}
        >
          <div className="relative h-48 overflow-hidden">
            <Image
              src={site.image}
              alt={site.alt}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div
              className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold"
              style={{ background: '#6FD3E0', color: '#0B1622' }}
            >
              Dive Site
            </div>
            <div className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: 'rgba(0,0,0,0.70)', color: '#EAF4F8' }}>
              {site.difficulty}
            </div>
          </div>
          <div className="p-5 space-y-3">
            <h3 className="text-xl font-bold" style={{ color: '#EAF4F8' }}>{site.name}</h3>
            <p className="text-sm line-clamp-2" style={{ color: '#C8DDE6' }}>{site.description}</p>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="flex items-center space-x-1 px-2 py-1 rounded" style={{ background: 'rgba(111,211,224,0.10)', color: '#6FD3E0' }}>
                <Icon name="MapPinIcon" size={14} />
                <span>{site.regionName}</span>
              </span>
              <span className="flex items-center space-x-1 px-2 py-1 rounded" style={{ background: 'rgba(255,138,61,0.10)', color: '#FF8A3D' }}>
                <Icon name="ArrowTrendingDownIcon" size={14} />
                <span>{site.depth}</span>
              </span>
              <span
                className="flex items-center gap-1.5 px-2 py-1 rounded"
                style={{ background: `${accent}18`, color: accent, border: `1px solid ${accent}35` }}
              >
                {brandedIcon && (
                  <Image
                    src={brandedIcon}
                    alt={`${site.type} dive site icon`}
                    width={14}
                    height={14}
                    className="object-contain"
                    style={{ filter: 'brightness(1.1)' }}
                  />
                )}
                <span>{site.type}</span>
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  // Species Card
  if (result.type === 'species') {
    const species = result.data;
    return (
      <Link href="/marine-life" className="block group">
        <div
          className="rounded-xl overflow-hidden transition-all duration-200 h-full"
          style={cardStyle}
          onMouseEnter={(e) => cardHoverEnter(e.currentTarget as HTMLElement)}
          onMouseLeave={(e) => cardHoverLeave(e.currentTarget as HTMLElement)}
        >
          <div className="relative h-48 overflow-hidden">
            <Image
              src={species.image}
              alt={species.alt}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: '#6FD3E0', color: '#0B1622' }}>
              Marine Species
            </div>
            <div className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: 'rgba(0,0,0,0.70)', color: '#EAF4F8' }}>
              {species.sightingProbability}
            </div>
          </div>
          <div className="p-5 space-y-3">
            <h3 className="text-xl font-bold" style={{ color: '#EAF4F8' }}>{species.commonName}</h3>
            <p className="text-sm italic" style={{ color: '#C8DDE6' }}>{species.scientificName}</p>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="flex items-center space-x-1 px-2 py-1 rounded" style={{ background: 'rgba(111,211,224,0.10)', color: '#6FD3E0' }}>
                <Icon name="TagIcon" size={14} />
                <span>{species.category}</span>
              </span>
              <span className="flex items-center space-x-1 px-2 py-1 rounded" style={{ background: 'rgba(255,138,61,0.10)', color: '#FF8A3D' }}>
                <Icon name="ShieldCheckIcon" size={14} />
                <span>{species.conservationStatus}</span>
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  // Dive Centre Card
  if (result.type === 'dive-centre') {
    const centre = result.data;
    return (
      <div
        className="rounded-xl overflow-hidden transition-all duration-200 h-full"
        style={cardStyle}
        onMouseEnter={(e) => cardHoverEnter(e.currentTarget as HTMLElement)}
        onMouseLeave={(e) => cardHoverLeave(e.currentTarget as HTMLElement)}
      >
        {centre.logo && (
          <div className="p-6" style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="relative h-24 flex items-center justify-center">
              <Image src={centre.logo} alt={`${centre.name} logo`} width={200} height={96} className="object-contain" />
            </div>
          </div>
        )}
        <div className="p-5 space-y-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div
                className="inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-semibold mb-2"
                style={{ background: 'rgba(255,138,61,0.12)', color: '#FF8A3D', border: '1px solid rgba(255,138,61,0.20)' }}
              >
                <Icon name="BuildingOfficeIcon" size={14} />
                <span>Dive Centre</span>
              </div>
              <h3 className="text-xl font-bold" style={{ color: '#EAF4F8' }}>{centre.name}</h3>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="flex items-center space-x-1 px-2 py-1 rounded" style={{ background: 'rgba(111,211,224,0.10)', color: '#6FD3E0' }}>
              <Icon name="MapPinIcon" size={14} />
              <span>{centre.region}</span>
            </span>
            <span className="flex items-center space-x-1 px-2 py-1 rounded" style={{ background: 'rgba(111,211,224,0.10)', color: '#6FD3E0' }}>
              <Icon name="AcademicCapIcon" size={14} />
              <span>{centre.trainingAgency}</span>
            </span>
          </div>
          <div className="space-y-1">
            {centre.focus.slice(0, 3).map((item, idx) => (
              <p key={idx} className="text-xs flex items-start space-x-1" style={{ color: '#C8DDE6' }}>
                <Icon name="CheckCircleIcon" size={14} style={{ color: '#6FD3E0' }} className="mt-0.5 flex-shrink-0" />
                <span>{item}</span>
              </p>
            ))}
          </div>
          {centre.website && (
            <a
              href={centre.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 font-semibold text-sm mt-3 transition-colors duration-200"
              style={{ color: '#FF8A3D' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#ffaa6e'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#FF8A3D'; }}
            >
              <span>Visit Website →</span>
            </a>
          )}
        </div>
      </div>
    );
  }

  // Resource Card
  if (result.type === 'resource') {
    const resource = result.data;
    return (
      <div
        className="rounded-xl overflow-hidden transition-all duration-200 h-full"
        style={cardStyle}
        onMouseEnter={(e) => cardHoverEnter(e.currentTarget as HTMLElement)}
        onMouseLeave={(e) => cardHoverLeave(e.currentTarget as HTMLElement)}
      >
        <div className="p-6" style={{ background: 'rgba(111,211,224,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="relative h-24 flex items-center justify-center">
            <Image src={resource.logo} alt={`${resource.name} logo`} width={200} height={96} className="object-contain" />
          </div>
        </div>
        <div className="p-5 space-y-3">
          <div
            className="inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-semibold"
            style={{ background: 'rgba(111,211,224,0.12)', color: '#6FD3E0', border: '1px solid rgba(111,211,224,0.20)' }}
          >
            <Icon name="BookOpenIcon" size={14} />
            <span>Resource</span>
          </div>
          <h3 className="text-xl font-bold" style={{ color: '#EAF4F8' }}>{resource.name}</h3>
          <p className="text-sm line-clamp-3" style={{ color: '#C8DDE6' }}>{resource.description}</p>
          <div className="flex items-center space-x-2 text-xs">
            <span className="px-2 py-1 rounded" style={{ background: 'rgba(111,211,224,0.10)', color: '#6FD3E0' }}>
              {resource.category}
            </span>
          </div>
          <a
            href={resource.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 font-semibold text-sm mt-3 transition-colors duration-200"
            style={{ color: '#FF8A3D' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#ffaa6e'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#FF8A3D'; }}
          >
            <span>Visit Resource →</span>
          </a>
        </div>
      </div>
    );
  }

  return null;
};

export default SearchResultCard;